import { usesStripePayments } from "../../model/PaymentConfiguration";
import { usePayoutOverviewAccess } from "../../hooks/usePayoutOverviewAccess";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import Download from "@mui/icons-material/Download";
import OpenInBrowser from "@mui/icons-material/OpenInBrowser.js";
import { cloneElement, ReactElement } from "react";
import { DatagridConfigurable, FunctionField, List, TextField, useRecordContext } from "react-admin";
import { grants } from "../../backoffice.access_control.js";
import { RecordListButton } from "../../buttons/RecordListButton.js";
import { DateField } from "../../fields/DateField";
import { MoneyField } from "../../fields/MoneyField";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import type { AutovioPayoutRecord } from "../../providers/autovioPayoutProvider.js";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { CopyToClipboardButton } from "../../buttons/CopyToClipboardButton.js";
import { Alert, Box, Tooltip } from "@mui/material";
import { PageTitle } from "../../misc/PageTitle";
import { AccessDenied } from "../../misc/AccessDenied";
import { EmptyState } from "../../misc/EmptyState";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { getAuthenticatedBackendClient } from "../../api/backend.api";
import { MoneyCurrency } from "../../misc/Money";
import { reportError } from "../../backoffice.utils";
import { Row } from "../../misc/Row.js";

export function PayoutOverview({ className }: { className?: string }) {
  const storeKey = "AutovioPayoutOverview_v1";
  const drivingSchool = useRecordContext<DrivingSchool>();
  const { canView, isLoading, error } = usePayoutOverviewAccess(drivingSchool);
  const columns: ReactElement[] = [
    ...(grants.has("admin")
      ? [
          <FunctionField
            label="ID"
            source="id"
            render={(record: AutovioPayoutRecord) => <CopyToClipboardButton data={record.id} />}
            sortable={false}
          />,
        ]
      : []),
    <DateField label="Auszahlungsdatum" source="payoutDate" />,
    <MoneyField label={"Auszahlungs\u00ADhöhe"} source="calculatedPayoutAmount" sortable={false} />,
    <TextField label="Verwendungszweck" source="statementDescriptor" sortable={false} />,
    <FunctionField
      label="Abgerechnete Leistungen"
      render={(record: AutovioPayoutRecord) => record.items.length}
      sortable={false}
    />,
    <FunctionField
      label="Aktionen"
      render={(record: AutovioPayoutRecord) => {
        const hasPdf = !!record.pdf;
        const hasPdfDownload = !!record.pdfDownload;
        const hasDatevCsv = !!record.datevCsvDownload;
        const hasDatevZip = !!record.datevZipDownload;
        if (!hasPdf && !hasPdfDownload && !hasDatevCsv && !hasDatevZip) {
          return null;
        }
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            {hasPdf && <RecordListButton source="pdf" title="Öffnen" startIcon={<OpenInBrowser />} />}
            {hasPdfDownload && <RecordListButton source="pdfDownload" title="Download" startIcon={<Download />} />}
            {hasDatevCsv && <RecordListButton source="datevCsvDownload" title="DATEV CSV" startIcon={<Download />} />}
            {hasDatevZip && <RecordListButton source="datevZipDownload" title="DATEV ZIP" startIcon={<Download />} />}
          </Box>
        );
      }}
      sortable={false}
    />,
  ].map((it, index) => cloneElement(it, { key: index }));

  if (!grants.has("viewPayoutInformation")) {
    return (
      <>
        <PageTitle>Auszahlungen</PageTitle>
        <AccessDenied />
      </>
    );
  }

  if (!drivingSchool || isLoading) {
    return (
      <>
        <PageTitle>Auszahlungen</PageTitle>
        <LoadingIndicator />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageTitle>Auszahlungen</PageTitle>
        <Alert severity="error">Die Auszahlungen konnten nicht geladen werden.</Alert>
      </>
    );
  }

  if (!canView) {
    return (
      <>
        <PageTitle>Auszahlungen</PageTitle>
        <AccessDenied />
      </>
    );
  }

  const drivingSchoolId = drivingSchool.uid;

  return (
    <>
      <PageTitle>Auszahlungen</PageTitle>
      {usesStripePayments(drivingSchool) && <_NextPayoutInfo drivingSchoolId={drivingSchoolId} />}
      <List
        className={className}
        resource={"autovioPayouts"}
        storeKey={storeKey}
        title=" " // <-- prevent that the default list title is rendered
        filter={{ drivingSchoolId }}
        sort={{ field: "payoutDate", order: "DESC" }}
        exporter={false}
        empty={<EmptyState noEntriesText="Noch keine Auszahlung vorhanden." />}
      >
        <DatagridConfigurable
          preferenceKey={storeKey}
          bulkActionButtons={false}
          empty={
            <EmptyState
              noEntriesText="Noch keine Auszahlung vorhanden."
              noMatchingEntriesText="Keine passenden Auszahlungen gefunden."
            />
          }
        >
          {columns}
        </DatagridConfigurable>
      </List>
    </>
  );
}

function _NextPayoutInfo({ drivingSchoolId }: { drivingSchoolId: string }) {
  const [amount, setAmount] = useState<{ amount: string; currency: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextDate, setNextDate] = useState<string | null>(null);

  useEffect(() => {
    const getNextPayoutAmount = async () => {
      try {
        setLoading(true);
        const client = await getAuthenticatedBackendClient();
        const res = await client.payout.payoutNextKnownAmountRetrieve({ companyDrivingSchoolId: drivingSchoolId });
        setAmount(res.amount);
        setNextDate(res.next_payout_date ?? null);
      } catch (error) {
        setError("Fehler beim Laden des Auszahlungsbetrags");
        reportError("Fehler beim Laden des Auszahlungsbetrags", error);
      } finally {
        setLoading(false);
      }
    };
    void getNextPayoutAmount();
  }, [drivingSchoolId]);

  if (loading) {
    return (
      <Alert severity="info" style={{ margin: "20px 0" }}>
        Lädt den aktuellen Auszahlungswert...
      </Alert>
    );
  }
  if (error) {
    return (
      <Alert severity="warning" style={{ margin: "20px 0" }}>
        {error}
      </Alert>
    );
  }
  if (!amount) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
      <Alert severity="info" sx={{ flex: 1, alignItems: "center" }}>
        <Row alignItems="center">
          Aktueller Wert für nächste Auszahlung
          {nextDate ? <> (geplant am {DateTime.fromISO(nextDate).toFormat("d.L.yyyy")})</> : null}: &nbsp;
          <MoneyCurrency amount={amount.amount} currency={amount.currency} />{" "}
          <Tooltip
            title={
              "Hinweis: Der tatsächliche Wert für die kommende Auszahlung kann sich noch bis zum Tag der Auszahlung ändern. Wenn weitere Rechnungen noch bezahlt werden, wird der Wert steigen. Wenn Rechnungen ausgebucht oder Zahlungen von Kunden angefochten werden, korrigiert sich der Wert nach unten."
            }
          >
            <InfoOutlined color="action" sx={{ ml: 0.5 }} fontSize="small" />
          </Tooltip>
        </Row>
      </Alert>
    </Box>
  );
}
